import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { devLog } from '@/utils/logger';
import { formatApiError } from '@/services/api';
import channelsApi from '@/services/channels';
import {
  useCanvaChannels,
  useCanvaDesigns,
  useStartCanvaImport,
} from '@/hooks/useCanvaImport';
import { CanvaDesignForUI, CanvaExportOptions } from '@/types/canva';
import { CanvaAccountSelector } from './CanvaAccountSelector';
import { CanvaDesignBrowser } from './CanvaDesignBrowser';
import { CanvaExportConfig } from './CanvaExportConfig';

/**
 * CanvaImportDialog Component
 *
 * Multi-step modal for importing designs from Canva:
 * 1. select-account - Choose Canva channel
 * 2. browse - Grid view with search, sort, multi-select
 * 3. configure - Export options per design
 *
 * After starting import, the modal closes and SSE notifications handle progress.
 */

// Types for the multi-step flow
type Step = 'select-account' | 'browse' | 'configure' | 'started';

export interface SelectedDesign {
  design: CanvaDesignForUI;
  exportOptions: CanvaExportOptions;
}

interface CanvaImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (mediaIds: number[]) => void;
}

export const CanvaImportDialog = ({
  isOpen,
  onClose,
}: CanvaImportDialogProps) => {
  const { t } = useTranslation('common');

  // State
  const [step, setStep] = useState<Step>('select-account');
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  const [selectedDesigns, setSelectedDesigns] = useState<SelectedDesign[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'modified_descending' | 'title_ascending'>(
    'modified_descending'
  );
  const [importedCount, setImportedCount] = useState(0);

  // Hooks
  // includeInactive: true — a revoked Canva channel must still show up here so the
  // account selector can surface a reconnect prompt inside the modal, instead of the
  // split-button hiding the whole import entry point.
  const { data: channelsData, isLoading: channelsLoading, isError: channelsError, error: channelsErrorData } = useCanvaChannels({ includeInactive: true, enabled: isOpen });
  const channels = channelsData || [];
  const activeChannels = channels.filter((c) => c.isActive);
  // Channel(s) exist but none are active/usable — the account selector needs to
  // prompt reconnect rather than show an empty "connect Canva" state.
  const needsReconnect = activeChannels.length === 0 && channels.length > 0;

  // Canva ignores the query whenever a sort is sent, so a (trimmed) search term
  // switches to relevance ranking by omitting sortBy. The user's chosen sort is
  // kept in state and applies again once the search is cleared.
  const trimmedSearch = searchQuery.trim();
  const designsQuery = useCanvaDesigns({
    channelId: selectedChannelId,
    search: trimmedSearch || undefined,
    sortBy: trimmedSearch ? undefined : sortBy,
  });

  const startImport = useStartCanvaImport();

  // An empty page that still carries a continuation is not "no results": keep
  // fetching until a page has designs or Canva reports no more. Bounded by the
  // continuation chain; stops on error (no auto-retry).
  const { data: designsData, hasNextPage, isFetching, isError: designsError, fetchNextPage } = designsQuery;
  const loadedCount = designsData?.pages.reduce((n, p) => n + p.designs.length, 0) ?? 0;
  useEffect(() => {
    if (step !== 'browse' || designsError || isFetching) return;
    if (designsData && loadedCount === 0 && hasNextPage) {
      void fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, designsError, isFetching, designsData, loadedCount, hasNextPage]);

  /**
   * Reset state when dialog closes
   */
  const handleClose = useCallback(() => {
    setTimeout(() => {
      setStep('select-account');
      setSelectedChannelId(null);
      setSelectedDesigns([]);
      setSearchQuery('');
      setSortBy('modified_descending');
      setImportedCount(0);
    }, 300);
    onClose();
  }, [onClose]);

  /**
   * Trigger the Canva OAuth reconnect flow.
   * Fetches the OAuth URL from the same endpoint ChannelSettings uses
   * (GET /api/auth/canva) and redirects — the OAuth callback will land
   * back on Settings with ?success=canva_connected, identical to a fresh connect.
   */
  const handleReconnect = useCallback(async () => {
    try {
      const authUrl = await channelsApi.connectCanva();
      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (err) {
      devLog.error('Failed to initiate Canva reconnect:', err);
    }
  }, []);

  /**
   * Auto-skip the account selector when exactly one channel is available.
   *
   * Runs whenever:
   * - The dialog opens/closes (isOpen changes)
   * - Channels finish loading (channels.length changes)
   *
   * Guard: only auto-advance when the user is still on the select-account step,
   * so pressing "Back" from the browse step is never overridden.
   */
  useEffect(() => {
    if (!isOpen) return;
    if (channelsLoading) return;
    if (activeChannels.length !== 1) return;
    if (step !== 'select-account') return;

    // Exactly one active channel — auto-select it and jump to browse
    setSelectedChannelId(activeChannels[0].id);
    setStep('browse');
    // `step` is intentionally excluded from deps: including it would re-fire this effect
    // when the user clicks Back (which sets step back to 'select-account'), trapping them
    // on the browse step in a loop. The `step !== 'select-account'` guard inside handles
    // the read safely — we only care about the value at the moment isOpen/channels change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, channelsLoading, activeChannels.length]);

  /**
   * Clear search when switching accounts
   */
  useEffect(() => {
    if (selectedChannelId !== null) {
      setSearchQuery('');
    }
  }, [selectedChannelId]);

  /**
   * Handle design selection toggle
   */
  const toggleDesignSelection = (design: CanvaDesignForUI) => {
    const exists = selectedDesigns.find((d) => d.design.id === design.id);
    if (exists) {
      setSelectedDesigns(selectedDesigns.filter((d) => d.design.id !== design.id));
    } else {
      setSelectedDesigns([
        ...selectedDesigns,
        {
          design,
          exportOptions: {
            format: design.hasVideo ? 'mp4' : 'png',
            quality: 'full',
            mergeAsCarousel: design.isMultiPage,
          },
        },
      ]);
    }
  };

  /**
   * Select all visible designs
   */
  const handleSelectAll = (designs: CanvaDesignForUI[]) => {
    const newSelections = designs
      .filter((design) => !selectedDesigns.some((d) => d.design.id === design.id))
      .map((design) => ({
        design,
        exportOptions: {
          format: design.hasVideo ? 'mp4' : 'png',
          quality: 'full',
          mergeAsCarousel: design.isMultiPage,
        } as CanvaExportOptions,
      }));

    setSelectedDesigns([...selectedDesigns, ...newSelections]);
  };

  /**
   * Deselect all designs
   */
  const handleDeselectAll = () => {
    setSelectedDesigns([]);
  };

  /**
   * Update export options for a design
   */
  const updateExportOptions = (
    designId: string,
    options: Partial<CanvaExportOptions>
  ) => {
    setSelectedDesigns(
      selectedDesigns.map((d) =>
        d.design.id === designId
          ? { ...d, exportOptions: { ...d.exportOptions, ...options } }
          : d
      )
    );
  };

  /**
   * Start import - shows toast and closes modal
   */
  const handleStartImport = async () => {
    if (!selectedChannelId || selectedDesigns.length === 0) return;

    const request = {
      channelId: selectedChannelId,
      designs: selectedDesigns.map((d) => ({
        designId: d.design.id,
        title: d.design.title,
        thumbnailUrl: d.design.thumbnailUrl,
        exportOptions: d.exportOptions,
      })),
    };

    try {
      await startImport.mutateAsync(request);
      // Show started confirmation step
      setImportedCount(selectedDesigns.length);
      setStep('started');
    } catch (error) {
      devLog.error('Failed to start import:', error);
    }
  };

  /**
   * Render step content
   */
  const renderStepContent = () => {
    switch (step) {
      case 'select-account':
        return (
          <>
            {channelsError ? (
              <div className="text-center py-8">
                <Icon name={ICONS.CAUTION} size="lg" className="mx-auto mb-3 text-amber-500" ariaHidden />
                <p className="text-sm text-gray-700 font-medium mb-1">
                  {formatApiError(channelsErrorData).message}
                </p>
              </div>
            ) : (
              <CanvaAccountSelector
                channels={activeChannels}
                selectedChannelId={selectedChannelId}
                onSelect={setSelectedChannelId}
                isLoading={channelsLoading}
                onReconnect={handleReconnect}
                needsReconnect={needsReconnect}
              />
            )}
          </>
        );

      case 'browse':
        return (
          <CanvaDesignBrowser
            designs={designsQuery.data?.pages.flatMap((p) => p.designs) || []}
            selectedDesigns={selectedDesigns}
            onToggleSelect={toggleDesignSelection}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            searchQuery={trimmedSearch}
            onSearchChange={setSearchQuery}
            sortBy={sortBy}
            onSortChange={setSortBy}
            hasMore={designsQuery.hasNextPage || false}
            onLoadMore={() => designsQuery.fetchNextPage()}
            isLoading={designsQuery.isLoading}
            isFetchingMore={designsQuery.isFetchingNextPage}
            isError={designsQuery.isError}
            error={designsQuery.error}
            onRetry={() => void designsQuery.refetch()}
            onReconnect={handleReconnect}
          />
        );

      case 'configure':
        return (
          <CanvaExportConfig
            selectedDesigns={selectedDesigns}
            onUpdateOptions={updateExportOptions}
          />
        );

      case 'started':
        return (
          <div className="py-8 text-center">
            <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('canva_import_started_title', { defaultValue: 'Import Started!' })}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('canva_import_started_message', {
                count: importedCount,
                defaultValue_one: 'Your {{count}} design is being imported in the background.',
                defaultValue_other: 'Your {{count}} designs are being imported in the background.',
              })}
            </p>
            <p className="text-sm text-gray-500">
              {t('canva_import_notification_hint', {
                defaultValue: "You'll receive a notification when the import is complete.",
              })}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  /**
   * Render footer buttons
   */
  const renderFooter = () => {
    switch (step) {
      case 'select-account':
        return (
          <div className="flex justify-end gap-1.5 sm:gap-2">
            <CancelButton onClick={handleClose} tooltip="Cancel" />
            <Button
              variant="primary"
              onClick={() => setStep('browse')}
              disabled={!selectedChannelId}
            >
              {t('next')}
            </Button>
          </div>
        );

      case 'browse':
        return (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">
              {t('canva_designs_selected', { count: selectedDesigns.length })}
            </span>
            <div className="flex gap-1.5 sm:gap-2">
              <Button variant="secondary" onClick={() => setStep('select-account')}>
                {t('back')}
              </Button>
              <Button
                variant="primary"
                onClick={() => setStep('configure')}
                disabled={selectedDesigns.length === 0}
              >
                {t('next')}
              </Button>
            </div>
          </div>
        );

      case 'configure':
        return (
          <div className="flex justify-end gap-1.5 sm:gap-2">
            <Button variant="secondary" onClick={() => setStep('browse')}>
              {t('back')}
            </Button>
            <Button
              variant="primary"
              onClick={handleStartImport}
              loading={startImport.isPending}
            >
              {t('start_import')}
            </Button>
          </div>
        );

      case 'started':
        return (
          <div className="flex justify-end">
            <Button variant="primary" onClick={handleClose}>
              {t('done')}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  /**
   * Step titles
   */
  const stepTitles: Record<Step, string> = {
    'select-account': t('canva_select_account'),
    browse: t('canva_browse_designs'),
    configure: t('canva_configure_export'),
    started: t('canva_import_started', { defaultValue: 'Import Started' }),
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      title={stepTitles[step]}
      size="lg"
      footer={renderFooter()}
    >
      {renderStepContent()}
    </Modal>
  );
};
